import { useCallback, useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';
import { Link } from '@amzn/stencil-react-components/link';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
// standards-ignore: cross-feature — IconPlusSmall/IconBinSmall/IconExternalLinkSmall are generic add/remove/external-link affordances; a shared wrapper over a bare icon would add indirection without value
import {
  IconPlusSmall,
  IconBinSmall,
  IconExternalLinkSmall,
} from '@amzn/stencil-react-icons';
import type { DocLink } from 'src/types/helios/Question';

import { truncateStyle } from '../truncateStyle';

/** Column flex proportions shared by the header and every row so the
 *  Document / Link / Actions columns stay aligned. */
const COL_DOCUMENT_FLEX = 2;
const COL_LINK_FLEX = 3;
const COL_ACTIONS_FLEX = 1;

/** Single-line ellipsis clamp for a cell's text so a long value never wraps or
 *  blows out its column. Module-level for reference stability. */
const SINGLE_LINE = truncateStyle(1);

/**
 * One documentation reference: an optional display title and its URL. Reuses
 * the Helios `DocLink` contract so this field edits the real question shape
 * (single source of truth) rather than a forked copy.
 */
export type DocLinkItem = DocLink;

/** Visible copy — supplied by the caller so this stays i18n-bundle-agnostic
 *  and reusable across features. */
export interface DocLinksFieldLabels {
  /** Column header above the title column. */
  readonly documentColumnHeader: string;
  /** Column header above the link column. */
  readonly linkColumnHeader: string;
  readonly titlePlaceholder: string;
  readonly titleAriaLabel: string;
  readonly urlPlaceholder: string;
  readonly urlAriaLabel: string;
  readonly addButton: string;
  /** Accessible label prefix for each row's icon-only delete button (followed
   *  by the link's title/URL). */
  readonly removeAltText: string;
  readonly emptyText: string;
  readonly invalidUrlText: string;
  /** Inline error shown when the entered URL is already in the list. */
  readonly duplicateUrlText: string;
}

export interface DocLinksFieldProps {
  /** Current list of doc links. */
  readonly value: readonly DocLinkItem[];
  /** Emits the whole next list on add/remove. */
  readonly onChange: (next: DocLinkItem[]) => void;
  readonly labels: DocLinksFieldLabels;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/** Schemes accepted for a documentation link. A bare `new URL()` accepts ANY
 *  scheme (`referhttps:`, `foo:bar`, even `javascript:`), so a typo like
 *  `referhttps://…` slips through and renders a broken/unsafe href. We render
 *  the value into an anchor `href`, so restrict to real web links. */
const ALLOWED_URL_PROTOCOLS: readonly string[] = ['http:', 'https:'];

/** True when `raw` parses as an absolute http(s) URL. Stricter than the manual
 *  workflow's bare `new URL()` on purpose: the protocol allowlist rejects
 *  typo'd schemes and unsafe `javascript:` hrefs. */
function isValidUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    return ALLOWED_URL_PROTOCOLS.includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Repeatable documentation-link editor: a title + URL entry row, an Add
 * control, and the list of already-added links, each removable. The Stencil
 * equivalent of the manual workflow's SOP document-link table + modal, minus
 * the modal — inline add keeps the whole list visible while editing.
 *
 * URL is validated with `new URL()` before Add is enabled (title is
 * optional, matching the Helios `DocLink` shape where both fields are
 * optional). Fully controlled: the owner holds the list and this emits the
 * whole next array on every add/remove.
 */
export function DocLinksField({
  value,
  onChange,
  labels,
  disabled = false,
  dataTestId,
}: DocLinksFieldProps): JSX.Element {
  const [draftTitle, setDraftTitle] = useState('');
  const [draftUrl, setDraftUrl] = useState('');

  const trimmedUrl = draftUrl.trim();
  const urlLooksInvalid = trimmedUrl.length > 0 && !isValidUrl(trimmedUrl);
  // A URL already in the list can't be added again. Comparing
  // case-insensitively catches the common re-paste; keeping links unique also
  // makes each row's `key={item.link}` stable, so React never mis-associates
  // rows on removal.
  const isDuplicateUrl =
    isValidUrl(trimmedUrl) &&
    value.some((item) => item.link?.toLowerCase() === trimmedUrl.toLowerCase());
  const canAdd = !disabled && isValidUrl(trimmedUrl) && !isDuplicateUrl;

  const handleAdd = useCallback(() => {
    const url = draftUrl.trim();
    if (!isValidUrl(url)) return;
    // Reject a URL already present (defence-in-depth behind `canAdd`), so a
    // duplicate can never enter the list and collide on its React key.
    const isDup = value.some((item) => item.link?.toLowerCase() === url.toLowerCase());
    if (isDup) return;
    const title = draftTitle.trim();
    // Only include title when non-empty so it round-trips under
    // exactOptionalPropertyTypes (matches the manual workflow).
    const item: DocLinkItem = title ? { title, link: url } : { link: url };
    onChange([...value, item]);
    setDraftTitle('');
    setDraftUrl('');
  }, [draftTitle, draftUrl, value, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const handleUrlKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && canAdd) {
        e.preventDefault();
        handleAdd();
      }
    },
    [canAdd, handleAdd],
  );

  return (
    <Col gridGap="dimensions.spacing.300" width="100%">
      {/* Existing links, or the empty state when there are none. */}
      {value.length === 0 ? (
        <Text
          color="color.text.secondary"
          data-testid={dataTestId ? `${dataTestId}-empty` : undefined}
        >
          {labels.emptyText}
        </Text>
      ) : (
        <Col gridGap="dimensions.spacing.100">
          {/* Column header — aligns with each row's Document / Link columns
           *  (mirrors the manual workflow's SOP table headers). The trailing
           *  actions column has no header (a header over a single delete icon
           *  reads as misaligned). */}
          <Row gridGap="dimensions.spacing.300" alignItems="center">
            <Col flex={COL_DOCUMENT_FLEX} minWidth={0}>
              <Text fontWeight="bold" color="color.text.secondary" fontSize="T100">
                {labels.documentColumnHeader}
              </Text>
            </Col>
            <Col flex={COL_LINK_FLEX} minWidth={0}>
              <Text fontWeight="bold" color="color.text.secondary" fontSize="T100">
                {labels.linkColumnHeader}
              </Text>
            </Col>
            <Col flex={COL_ACTIONS_FLEX} minWidth={0} />
          </Row>

          {value.map((item, index) => (
            <Row
              // eslint-disable-next-line @eslint-react/no-array-index-key -- the list never reorders and removal is already index-based (handleRemove(index)); an index key is collision-proof even for caller-supplied data with duplicate/empty links, where link/title-based keys would collide
              key={index}
              alignItems="center"
              gridGap="dimensions.spacing.300"
              data-testid={dataTestId ? `${dataTestId}-row-${index}` : undefined}
            >
              {/* Document column — the title (or a dash when untitled). */}
              <Col flex={COL_DOCUMENT_FLEX} minWidth={0}>
                <Text css={SINGLE_LINE}>{item.title ?? '—'}</Text>
              </Col>
              {/* Link column — the URL as a new-tab link + external glyph,
               *  clamped to one line so it never wraps under the title. */}
              <Col flex={COL_LINK_FLEX} minWidth={0}>
                {item.link && isValidUrl(item.link) ? (
                  // Safe scheme → clickable link.
                  <Row alignItems="center" gridGap="dimensions.spacing.50" minWidth={0}>
                    <Link
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      css={SINGLE_LINE}
                    >
                      {item.link}
                    </Link>
                    <IconExternalLinkSmall aria-hidden="true" />
                  </Row>
                ) : item.link ? (
                  // Present but a disallowed scheme (e.g. `javascript:`): the
                  // add path validates typed URLs, but `value` items can arrive
                  // from the agent / persisted data / tests and never pass
                  // through it. Rendering such a value into an anchor `href`
                  // would be a live XSS vector, so show it as inert text.
                  <Text css={SINGLE_LINE}>{item.link}</Text>
                ) : null}
              </Col>
              {/* Actions column — an icon-only delete (trash), aligned to the
               *  row's end. The accessible name carries the intent. */}
              <Col flex={COL_ACTIONS_FLEX} minWidth={0} alignItems="flex-end">
                <Button
                  size={ButtonSize.Small}
                  variant={ButtonVariant.Tertiary}
                  icon={<IconBinSmall aria-hidden="true" />}
                  aria-label={`${labels.removeAltText} ${item.title ?? item.link ?? ''}`.trim()}
                  disabled={disabled}
                  onClick={() => handleRemove(index)}
                  data-testid={dataTestId ? `${dataTestId}-remove-${index}` : undefined}
                />
              </Col>
            </Row>
          ))}
        </Col>
      )}

      {/* Add row: title (optional) + URL (validated) + Add. */}
      <Row gridGap="dimensions.spacing.200" alignItems="flex-start" flexWrap="wrap">
        <Col flex={1} minWidth={0} gridGap="dimensions.spacing.50">
          <Input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder={labels.titlePlaceholder}
            aria-label={labels.titleAriaLabel}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-title-input` : undefined}
          />
        </Col>
        <Col flex={1} minWidth={0} gridGap="dimensions.spacing.50">
          <Input
            type="url"
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
            onKeyDown={handleUrlKeyDown}
            placeholder={labels.urlPlaceholder}
            aria-label={labels.urlAriaLabel}
            aria-invalid={urlLooksInvalid || isDuplicateUrl}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-url-input` : undefined}
          />
          {urlLooksInvalid || isDuplicateUrl ? (
            <Text
              color="color.status.critical-icon"
              fontSize="T100"
              data-testid={dataTestId ? `${dataTestId}-url-error` : undefined}
            >
              {urlLooksInvalid ? labels.invalidUrlText : labels.duplicateUrlText}
            </Text>
          ) : null}
        </Col>
        <Button
          size={ButtonSize.Small}
          variant={ButtonVariant.Secondary}
          icon={<IconPlusSmall aria-hidden="true" />}
          disabled={!canAdd}
          onClick={handleAdd}
          data-testid={dataTestId ? `${dataTestId}-add` : undefined}
        >
          {labels.addButton}
        </Button>
      </Row>
    </Col>
  );
}
